import React, { useContext, useMemo, useState } from 'react';
import { EntityStatus } from '../../refData/EntityStatus';
import styles from '../../styles/cr.module.scss';
import { IUser, User, ISpecificEntityFormProps, EntityValidations, ZCoreUserGroupMember } from '../../types';
import { DataContext } from '../DataContext';
import { CrDropdown } from '../cr/CrDropdown';
import { CrTextField } from '../cr/CrTextField';
import { EntityForm } from '../EntityForm';
import { isValidEmailAddress, isValidGuid, LookupService } from '../../services';
import { IntegrationContext } from '../IntegrationContext';
import { AuthenticationTypes } from '../../refData/AuthenticationTypes';
import { OrbUserContext } from '../OrbUserContext';
import { AppGlobals } from '../../AppGlobals';
import { SectorType } from '../../types/AppGlobals';

export class UserValidations extends EntityValidations {
    public Username: string = null;
    public UserTitle: string = null;
    public Firstname: string = null;
    public Surname: string = null;
    public EmailAddress: string = null;
    public Password: string = null;
    public AuthenticationType = null;
}

export const UserForm = (props: ISpecificEntityFormProps): React.ReactElement => {
    const { disableUserManagement } = useContext(IntegrationContext);
    const { orbConfig } = useContext(OrbUserContext);
    const { dataServices: { userService, accountService, zCoreUserGroupMemberService }, lookupData, loadLookupData: { entityStatuses, zCoreUserGroups } } = useContext(DataContext);

    //const [passwordAction, setPasswordAction] = useState<number | string>(1);
    const [emailExistsCoreDb, setEmailExistsCoreDb] = useState<boolean>(null);

    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => zCoreUserGroups(), [zCoreUserGroups]);

    const validateEntity = async (user: IUser): Promise<UserValidations> => {
        const errors = new UserValidations();
        const [matchingUsernames, matchingEmails] = await Promise.all([userService.readUsersByUsername(user.Username), userService.readUsersByEmailAddress(user.EmailAddress)]);

        // if (user.Username == null || user.Username === '') {
        //     errors.Username = 'Username is required';
        //     errors.Valid = false;
        // } else if (!user.IsServiceAccount && !isValidEmailAddress(user.Username)) {
        //     errors.Username = 'Username must be in a valid user@domain.gov.uk format';
        //     errors.Valid = false;
        // } else if (user.IsServiceAccount && !isValidGuid(user.Username)) {
        //     errors.Username = 'Username must be in a valid GUID (00000000-0000-0000-0000-000000000000) format';
        //     errors.Valid = false;
        // } else if (matchingUsernames?.length > 0 && matchingUsernames?.some(u => u.ID !== user.ID)) {
        //     errors.Username = 'Username already exists';
        //     errors.Valid = false;
        // } else {
        //     errors.Username = null;
        // }

        // if (user.Title == null || user.Title === '') {
        //     errors.Title = 'Display name is required';
        //     errors.Valid = false;
        // }
        // else {
        //     errors.Title = null;
        // }

        if (user.UserTitle == null || user.UserTitle.trim() === '') {
            errors.UserTitle = 'Title is required';
            errors.Valid = false;
        }
        else {
            errors.UserTitle = null;
        }

        if (user.Firstname == null || user.Firstname.trim() === '') {
            errors.Firstname = 'First name is required';
            errors.Valid = false;
        }
        else {
            errors.Firstname = null;
        }

        if (user.Surname == null || user.Surname.trim() === '') {
            errors.Surname = 'Surname is required';
            errors.Valid = false;
        }
        else {
            errors.Surname = null;
        }

        if (!user.IsServiceAccount && (user.EmailAddress == null || user.EmailAddress === '')) {
            errors.EmailAddress = 'Email address is required';
            errors.Valid = false;
        } else if (!user.IsServiceAccount && !isValidEmailAddress(user.EmailAddress)) {
            errors.EmailAddress = 'Email address must be a valid email address';
            errors.Valid = false;
        } else if (!user.IsServiceAccount && matchingEmails?.length > 0 && matchingEmails?.some(u => u.ID !== user.ID)) {
            errors.EmailAddress = 'Email address already exists';
            errors.Valid = false;
        } else {
            errors.EmailAddress = null;
        }
        if (user.AuthenticationTypeID === null) {
            errors.AuthenticationType = 'Authentication Type is required';
            errors.Valid = false;
        }
        else {
            errors.AuthenticationType = null;
        }
        if (emailExistsCoreDb !== null && user.AuthenticationTypeID === 1 && (user.PasswordActionID === 1 || user.PasswordActionID === 3)) {
            if (user.Password == null || user.Password === '') {
                errors.Password = 'Password is required';
                errors.Valid = false;
            }
            else
                errors.Password = null;

        }
        else {
            errors.Password = null;
        }

        return errors;
    };

    const passwordActions = [
        { key: 1, text: 'Set Password' },
        { key: 2, text: 'None' },
        { key: 3, text: 'Reset Password' },
    ];

    const getPasswordActions = () => {
        if (emailExistsCoreDb === false)
            return passwordActions.filter(x => x.key === 1);
        else if (emailExistsCoreDb === true) {
            return passwordActions.filter(x => x.key > 1);
        }
        return [];
    }

    const checkEmailOnCoreDb = async (email: string): Promise<boolean> => {
        const emailExist: boolean = await accountService.emailExistOnCoreUsers(email);
        setEmailExistsCoreDb(emailExist);
        return emailExist;
    }

    return (
        <EntityForm<IUser, UserValidations>
            {...props}
            entityName="User"
            renderFormFields={({ changeTextField, changeNumberField, changeCheckbox, changeDropdown, changeMultiDropdown, changeStatusDropdown, clearField }, formState) => {
                const { FormData: user, ValidationErrors: errors } = formState;

                const updateDisplayName = (
                    firstname: string,
                    surname: string,
                    changeTextField: (value: string, field: string) => void
                ) => {
                    const displayName = `${firstname || ''} ${surname || ''}`.trim();
                    changeTextField(displayName, 'Title');
                };

                return (
                    <div>
                        {/* <CrTextField
                            label="Username"
                            className={styles.formField}
                            required={true}
                            maxLength={255}
                            placeholder="E.g. john.smith@yourdomain.com"
                            value={user.Username}
                            onChange={(ev, newValue) => changeTextField(newValue, 'Username')}
                            errorMessage={errors.Username}
                        /> */}

                        <CrDropdown
                            label="Title"
                            className={styles.formField}
                            options={[
                                { key: "Mr", text: "Mr" },
                                { key: "Mrs", text: "Mrs" },
                                { key: "Ms", text: "Ms" },
                                { key: "Dr", text: "Dr" }
                            ]}
                            selectedKey={user.UserTitle}
                            onChange={(_, o) => changeDropdown(o, 'UserTitle')}
                            required={true}
                            errorMessage={errors.UserTitle}
                        />

                        <CrTextField
                            label="First name"
                            className={styles.formField}
                            required={true}
                            errorMessage={errors.Firstname}
                            maxLength={255}
                            value={user.Firstname || ''}
                            onChange={(ev, newValue) => {
                                changeTextField(newValue, 'Firstname');
                                updateDisplayName(newValue, user.Surname, changeTextField);
                            }}
                        />

                        <CrTextField
                            label="Surname"
                            className={styles.formField}
                            required={true}
                            errorMessage={errors.Surname}
                            maxLength={255}
                            value={user.Surname || ''}
                            onChange={(ev, newValue) => {
                                changeTextField(newValue, 'Surname');
                                updateDisplayName(user.Firstname, newValue, changeTextField);
                            }}
                        />


                        {/* <CrTextField
                            label="Display name"
                            className={styles.formField}
                            required={true}
                            maxLength={255}
                            value={user.Title}
                            onChange={(ev, newValue) => changeTextField(newValue, 'Title')}
                            errorMessage={errors.Title}
                        /> */}
                        {/* <CrCheckbox
                            label="User is an app/service account?"
                            className={styles.formField}
                            disabled={disableUserManagement && user.ID !== 0}
                            checked={user.IsServiceAccount}
                            onChange={(_, c) => changeCheckbox(c, 'IsServiceAccount', () => clearField('EmailAddress'))}
                        /> */}
                        <CrTextField
                            label="Email address"
                            className={styles.formField}
                            required={!user.IsServiceAccount}
                            maxLength={255}
                            disabled={(disableUserManagement && user.ID !== 0) || user.IsServiceAccount}
                            placeholder="E.g. john.smith@yourdomain.com"
                            value={user.EmailAddress || ''}
                            onChange={(ev, newValue) => changeTextField(newValue, 'EmailAddress')}
                            errorMessage={errors.EmailAddress}
                        />

                        <CrTextField
                            label="Phone"
                            className={styles.formField}
                            maxLength={100}
                            value={user.Phone || ''}
                            onChange={(ev, newValue) => changeTextField(newValue, 'Phone')}
                        />

                        <CrDropdown
                            label="Authentication Type"
                            className={styles.formField}
                            required={true}
                            errorMessage={errors.AuthenticationType}
                            options={[{ key: AuthenticationTypes.ZedX, text: 'ZedX' }, { key: AuthenticationTypes.Azure, text: 'Azure' }]}
                            selectedKey={user.AuthenticationTypeID}
                            onChange={(_, o) => {
                                changeDropdown(o, 'AuthenticationTypeID', null, async (u) => {
                                    if (u.AuthenticationTypeID === AuthenticationTypes.ZedX) {
                                        if (!isValidEmailAddress(u.EmailAddress)) {
                                            console.log('invalid email address');
                                        }
                                        else {
                                            const emailExist = await checkEmailOnCoreDb(u.EmailAddress);
                                            if (emailExist == true) {
                                                changeNumberField(2, 'PasswordActionID');//default None
                                            }

                                        }

                                    }

                                });

                            }}
                        />
                        {emailExistsCoreDb !== null && user.AuthenticationTypeID === 1 &&
                            <CrDropdown
                                label="Password Actions"
                                className={styles.formField}
                                options={getPasswordActions()}
                                selectedKey={user.PasswordActionID}
                                onChange={(_, o) => {
                                    console.log(o.key);
                                    changeDropdown(o, 'PasswordActionID')
                                    //setPasswordAction(o.key);
                                }}
                            />
                        }
                        {emailExistsCoreDb !== null && user.AuthenticationTypeID === 1 && (user.PasswordActionID === 1 || user.PasswordActionID === 3) &&
                            <CrTextField
                                label="Password"
                                type='password'
                                canRevealPassword
                                className={styles.formField}
                                required={true}
                                maxLength={255}
                                autoComplete="new-password"
                                value={user.Password}
                                onChange={(ev, newValue) => changeTextField(newValue, 'Password')}
                                errorMessage={errors.Password}
                            />
                        }
                        <CrDropdown
                            label="User Group(s)"
                            className={styles.formField}
                            multiSelect
                            options={LookupService.entitiesToSelectableOptions(lookupData.ZCoreUserGroups)}
                            selectedKeys={user.ZCoreUserGroupMembers?.map(m => m.ZCoreUserGroupID)}
                            onChange={(_, v) => changeMultiDropdown(v, 'ZCoreUserGroupMembers', new ZCoreUserGroupMember(), 'ZCoreUserGroupID')}
                        //disabled={risk.IsProjectRisk && risk.CustomFields.filter(x => x.CustomFieldDefnID === 1000).length > 0}
                        />

                        {[SectorType.Schools, SectorType.Colleges, SectorType.Universities].includes(orbConfig.CoreSectorID) && (
                            <CrDropdown
                                label="User Type"
                                className={styles.formField}
                                options={[
                                    { key: 'Management', text: 'Management' },
                                    { key: 'Operations', text: 'Operations' },
                                    { key: 'Teaching', text: 'Teaching' },
                                    { key: 'Student', text: 'Student' },
                                ]}
                                selectedKey={user.UserType}
                                onChange={(_, o) => {
                                    if (o.key === 'Student') {
                                        changeTextField('Student', 'JobRole');
                                    }
                                    changeDropdown(o, 'UserType');
                                }}
                            />
                        )}

                        <CrTextField
                            label="Job Role"
                            className={styles.formField}
                            //required={true}
                            maxLength={255}
                            value={user.JobRole}
                            onChange={(ev, newValue) => changeTextField(newValue, 'JobRole')}
                            disabled={user.UserType === 'Student' ? true : false}
                        //errorMessage={errors.Title}
                        />

                        {user.ID !== 0 &&
                            <CrDropdown
                                label="Account status"
                                className={styles.formField}
                                required={true}
                                options={[{ key: EntityStatus.Open, text: 'Enabled' }, { key: EntityStatus.Closed, text: 'Disabled' }]}
                                selectedKey={user.EntityStatusID}
                                onChange={(_, o) => changeStatusDropdown(o, 'EntityStatusID')}
                            />
                        }

                    </div>
                );
            }}
            loadEntity={(id) => {
                console.log('in load');
                return userService.read(id, true, true);
            }}
            onAfterLoad={async (entity) => {
                console.log('on after load', entity);
                await checkEmailOnCoreDb(entity.EmailAddress);
            }}
            loadNewEntity={() => new User()}
            loadEntityValidations={() => new UserValidations()}
            onValidateEntity={validateEntity}
            onCreate={u => userService.create(u)}
            onUpdate={u => userService.update(u.ID, u)}
            closeEntityConfirm={{
                header: `Are you sure you want to disable this user account?`,
                text: `All the permissions for this user will be deleted. `
                    + `If you need to re-enable this user account, you will also need to set up its user permissions.`
            }}
            childEntities={[
                {
                    ObjectParentProperty: 'ZCoreUserGroupMembers',
                    ParentIdProperty: 'UserID',
                    ChildIdProperty: 'ZCoreUserGroupID',
                    ChildService: zCoreUserGroupMemberService
                },
            ]}
        />
    );
};
